import {queryEditorsPick} from '@/database/editors-pick';
import {withRedirects} from '@/lib/withRedirects';
import Bugsnag from '@bugsnag/js';
import Head from 'next/head';
import {ReactElement, useRef, useState} from 'react';

import {get} from '@/services/artfinder-service';
import cacheStore from '@/services/cache-service';
import {validateParams} from '@/services/plp-url-params';

import constantsFactory from '@/utils/constants';
import {fetchJson} from '@/utils/fetch';
import paramsHelper from '@/utils/paramsHelper';

import galleryActions from '@/redux/actions/galleryActions';
import {paginationUsedByUser, sortUsedByUser, urlChanged} from '@/redux/actions/trackingActions';

import useAuth from '@/hooks/use-auth/use-auth';
import useForcePageRefresh from '@/hooks/use-force-page-refresh/use-force-page-refresh';

import Image from '@/atoms/Image/Image';
import CtaLink from '@/atoms/Links/CTA/CtaLink';
import TextLink from '@/atoms/Links/Text/TextLink';
import Gallery from '@/organisms/Gallery/Gallery';
import PageLayout from '@/organisms/Layout/Page/PageLayout';
import {selectUserSettingsData} from '@/redux/reducers/userSettingsReducer';
import {keyFilter} from '@/utils/helpers/string';
import {useSelector} from 'react-redux';
import StyledCollectionPage, {
  StyledContainer,
  StyledHero,
  StyledIntro,
  StyledNextCollectionLink,
} from './styles';

const truncate = (length) => (str) => {
  if (str.length <= length) {
    return str;
  }

  return str.substr(0, length) + '...';
};
const truncate200 = truncate(200);

const {ROLES} = constantsFactory();

const CollectionPage = ({
  collectionSlug,
  editorsPicks,
  initPage,
  params,
  initialProductSearchResponse,
  collectionInfo,
}) => {
  const auth = useAuth();
  const userSettings = useSelector(selectUserSettingsData);
  const activePage = useRef(initPage);
  const prevPage = useRef(activePage.current - 1);
  const nextPage = useRef(activePage.current + 1);
  const [canonicalBase, setCanonicalBase] = useState(params.join('/'));

  useForcePageRefresh();

  const buildCanonicalBase = (urlParams) => {
    let page = urlParams.find((param) => param.includes('page-'));
    page = page ? parseInt(page.replace('page-', '')) : 1;
    const canonicalBase = urlParams.filter((param) => !param.includes('page-')).join('/');
    activePage.current = page;
    nextPage.current = page + 1;
    prevPage.current = page - 1;
    setCanonicalBase(canonicalBase);
  };

  const canonicalString = (page) => {
    return `${process.env.HOST}/staff-picks/${collectionSlug} ${page > 1 ? '/page-' + page : ''}/`;
  };

  const formatDescription = (input) => {
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const output: (string | ReactElement)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(input)) !== null) {
      const textBeforeMatch = input.substring(lastIndex, match.index);
      if (textBeforeMatch) {
        output.push(textBeforeMatch);
      }

      output.push(
        <TextLink
          key={match.index}
          href={match[2]}
          title={`View ${match[1]} gallery`}
          target="_blank"
        >
          {match[1]}
        </TextLink>,
      );

      lastIndex = regex.lastIndex;
    }

    const remainingText = input.substring(lastIndex);
    if (remainingText) {
      output.push(remainingText);
    }

    return output;
  };

  const getLocaleCollectionInfo = (type: 'title' | 'description') => {
    const defaultLanguage = 'en-gb';
    const info =
      collectionInfo[type][keyFilter(userSettings.language)] ??
      collectionInfo[type][keyFilter(defaultLanguage)];
    return info;
  };

  return (
    <StyledCollectionPage className="collection">
      <Head>
        <title>
          {`${getLocaleCollectionInfo('title')} | Artfinder`}
          {activePage.current > 1 ? ` | ${activePage.current} page` : ''}
        </title>
        <meta name="description" content={`${getLocaleCollectionInfo('description')}`} />

        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonicalString(activePage.current)}></link>

        <meta property="og:site_name" content="Artfinder" />
        <meta property="og:title" content={`${getLocaleCollectionInfo('title')} | Artfinder`} />
        <meta
          property="og:description"
          content={truncate200(getLocaleCollectionInfo('description'))}
        />
        <meta property="og:url" content={canonicalString(activePage.current)} />
        <meta property="og:image" content={collectionInfo.hero_image} />

        <meta name="twitter:image" content={collectionInfo.hero_image} />
        <meta
          name="twitter:description"
          content={truncate200(getLocaleCollectionInfo('description'))}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@artfinder" />
      </Head>

      <PageLayout editorsPicks={editorsPicks}>
        <main>
          <StyledHero>
            <Image path={collectionInfo.hero_image} alt={'Cover image'} width={1400} height={380} />
            {auth.isAuthenticated() && auth.isAuthorised([ROLES.STAFF]) && (
              <CtaLink href={`/admin/`} target="_blank">
                Edit ...
              </CtaLink>
            )}
          </StyledHero>

          <StyledContainer>
            <StyledIntro>
              <Image
                height={115}
                width={115}
                path={collectionInfo.author_image}
                alt={`${getLocaleCollectionInfo('title')} presented to you by Artfinder`}
                retina
              />

              <h1>{getLocaleCollectionInfo('title')}</h1>

              <p>
                <span>{formatDescription(getLocaleCollectionInfo('description'))}</span>

                <small>
                  {collectionInfo.byline} |{' '}
                  <TextLink href={'/editors-picks/'} title={`Editors' Picks`}>
                    Editors' Picks
                  </TextLink>
                </small>
              </p>
            </StyledIntro>

            <Gallery
              itemsPerCall={12}
              itemsEagerLoadedPerCall={4}
              reduxActions={{
                seedSearch: galleryActions({galleryType: 'collection', gallerySlug: collectionSlug})
                  .seedSearch,
                filters: {
                  search: galleryActions({galleryType: 'collection', gallerySlug: collectionSlug})
                    .search,
                  clearSearch: galleryActions({
                    galleryType: 'collection',
                    gallerySlug: collectionSlug,
                  }).clearSearchResults,
                  GA: {
                    sortingUsed: sortUsedByUser,
                  },
                },
                GA: {paginationUsed: paginationUsedByUser, urlChanged: urlChanged},
              }}
              returnUrlParams={buildCanonicalBase}
              basePath={`/staff-picks/${collectionSlug}/`}
              urlParams={`${canonicalBase}`}
              initData={{page: initPage, searchResult: initialProductSearchResponse}}
              showCounter
            />

            {!!collectionInfo.cta_link && !!collectionInfo.cta_text && (
              <StyledNextCollectionLink href={collectionInfo.cta_link}>
                {collectionInfo.cta_text}
              </StyledNextCollectionLink>
            )}
          </StyledContainer>
        </main>
      </PageLayout>
    </StyledCollectionPage>
  );
};

export default CollectionPage;

const getInitialProductSearch = async (validatedParams, collectionSlug) => {
  const {API, TIMEOUT} = constantsFactory();
  const maxCallsPerPage = 5;
  const page = validatedParams.page ?? 1;
  const callNumber = (page - 1) * maxCallsPerPage + 1;
  const params = {...validatedParams, page: callNumber};
  const query = paramsHelper(params).objectToQueryString();
  const SEARCH_ENDPOINT = `${process.env.HOST}${API.COLLECTION_API}${collectionSlug}/${query}`;

  return fetchJson(SEARCH_ENDPOINT, {timeout: TIMEOUT.PRODUCT_SEARCH_API_TIMEOUT})
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.log('WARNING. Collection initial product fetch: ', err);
      return null;
    });
};

// eslint-disable-next-line valid-jsdoc
/**
 *
 * @return {Object} Properties for page
 */
export const getServerSideProps = withRedirects(async (context) => {
  try {
    const params = [];
    const collectionSlug = context.params?.param?.[0];
    const getPage = () => {
      const defaultPage = 1;
      let page: number | null = null;

      const params = context.query.param;
      if (params && Array.isArray(params)) {
        params.every((param) => {
          const [key, value] = param.split(/-(.+)/).map((el) => el.replace(/\s+/g, ' ').trim());
          if (key === 'page' && Number.isInteger(Number(value)) && Number(value) > 0) {
            page = Number(value);
            return false;
          }
          return true;
        });
      }

      return page ? page : defaultPage;
    };
    const initPage = getPage();
    const rawParams = context.query.param ? [...context.query.param] : [];
    const validatedParams = paramsHelper(validateParams(rawParams)).unwrapCategories().result;
    const initialProductSearchResponse = await getInitialProductSearch(
      validatedParams,
      collectionSlug,
    );

    // Get editors pick from api or from db according to settings
    const {CACHE} = constantsFactory();

    // redis cache
    let editorsPickFromCache = null;
    try {
      editorsPickFromCache = await cacheStore.get('last_5_editors_pick');
    } catch (e) {
      console.log(`[Product] Error. editors pick cache get() - ${e}`);
    }

    const promises: any[] = [];
    const collectionInfoAPICall = get(`/api/v2/collection/${collectionSlug}/info/`);
    promises.push(collectionInfoAPICall.then((r) => ({name: 'info', result: r})));

    if (!editorsPickFromCache) {
      const editorsPickCall = queryEditorsPick();
      promises.push(editorsPickCall.then((r) => ({name: 'editorsPicks', result: r})));
    }

    const result = await Promise.all(promises);
    const editorsPicks =
      editorsPickFromCache ?? result.filter((x) => x.name == 'editorsPicks')?.[0]?.result;
    const collectionInfo = result.filter((x) => x.name == 'info')?.[0]?.result;

    // set on cache anything we want
    if (!editorsPickFromCache) {
      try {
        await cacheStore.set(
          'last_5_editors_pick',
          editorsPicks,
          CACHE.EDITORS_PICK_CACHE_TTL_MILLISECONDS,
        );
      } catch (e) {
        console.log(`[Product] Error. editors pick cache set() - ${e}`);
      }
    }

    return {
      props: {
        collectionSlug,
        editorsPicks,
        initPage,
        params,
        initialProductSearchResponse,
        collectionInfo,
      },
    };
  } catch (e) {
    console.log(`[Collections] Error. Invalid Rendering - page: [${context.resolvedUrl}]`);
    console.log(e);
    Bugsnag.notify(
      new Error(`[Collections] Error. Invalid Rendering - page: [${context.resolvedUrl}]`),
      (event) => {
        event.severity = 'error';
        event.unhandled = true;
      },
    );

    // TODO: sending 404 not found because returning the error is causing infinite loop
    return {
      notFound: true,
    };
  }
});
